import React, { useState, useRef } from 'react';
import { Button, Space, Spin } from 'antd';
import s from './s.module.less';
import { usePayoutAccount } from 'hooks/paymentSetup/usePayoutAccount';
import type { PayoutAccount } from 'types/paymentSetup';
import { trackHeapEvent } from 'utils/analytics';
import PayoutSetupModal from './components/PayoutSetupModal';
import BillPayoutSetupModal from './components/BillPayoutSetupModal';
import { getProviderBillPayoutDetail, submitProviderBillPayoutToBill } from 'api/appointmentSetting';
import type { ProviderBillPayoutDetailVo } from 'types/providerBill';
import BillingPoliciesStep from 'pages/PaymentSetup/components/BillingPoliciesStep';
import ConsentFormsStep from 'pages/PaymentSetup/components/ConsentFormsStep';
import PracticeProfileStep from 'pages/PaymentSetup/components/PracticeProfileStep';
import BillingPayoutReadOnly from 'components/BillingPayoutReadOnly';

type Props = Readonly<{
    onBack: () => void;
    onSubmit: () => void;
    onGoToPayoutSetup?: () => void;
    hasStripeConnectAccount?: boolean;
}>;

function isPayoutSetUp(data: PayoutAccount): boolean {
    return !!(data.accountNumber || data.routingNumber || data.bankName);
}

function PayoutAccountCard({
    data,
    onSetUp,
    onSetUpIfNotReady,
}: Readonly<{
    data: PayoutAccount;
    onSetUp?: () => void;
    onSetUpIfNotReady?: () => void;
}>) {
    const hasSetUp = isPayoutSetUp(data);
    return (
        <div className={s.card}>
            <div className={s.cardHeader}>
                <span className={s.cardTitle}>Payout account</span>
                <Button className={s.editBtn} onClick={hasSetUp ? onSetUp : onSetUpIfNotReady}>
                    {hasSetUp ? 'Edit' : 'Set up now'}
                </Button>
            </div>
            <div className={s.cardBody}>
                {hasSetUp ? (
                    <div className={s.infoBlock}>
                        <div className={s.infoBlockTitle}>Connected</div>
                        <p className={s.infoText}>
                            Your bank account ending in ****{data.accountNumber?.slice(-4)} is connected.
                        </p>
                    </div>
                ) : (
                    <div className={s.infoBlock}>
                        <div className={s.infoBlockTitle}>Not set up</div>
                        <p className={s.infoText}>
                            You chose to set up your bank account later. That&apos;s okay — you can complete this step
                            anytime from the <strong>Financials</strong> tab when you&apos;re ready. You won&apos;t
                            receive payouts until a bank account is connected.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

function BillPayoutAccountCard({
    data,
    onSetUp,
}: Readonly<{
    data?: ProviderBillPayoutDetailVo;
    onSetUp?: () => void;
}>) {
    const hasPartial = !!(
        data?.firstName ||
        data?.lastName ||
        data?.email ||
        data?.addressLine1 ||
        data?.companyName ||
        data?.taxId ||
        data?.accountNumber ||
        data?.routingNumber
    );

    return (
        <div className={s.card}>
            <div className={s.cardHeader}>
                <span className={s.cardTitle}>Payout account</span>
                <Button className={s.editBtn} onClick={onSetUp}>
                    {hasPartial ? 'Edit' : 'Set up now'}
                </Button>
            </div>
            <div className={s.cardBody}>
                <BillingPayoutReadOnly data={data} />
            </div>
        </div>
    );
}

const SECTIONS = [
    { id: 1, title: 'Appointment billing policies' },
    { id: 2, title: 'Practice profile' },
    { id: 3, title: 'Practice address' },
    { id: 4, title: 'Consent forms' },
] as const;

export default function ReviewSettings(props: Props) {
    const { onBack, onSubmit, onGoToPayoutSetup, hasStripeConnectAccount } = props;
    const useStripe = hasStripeConnectAccount === true;
    const [payoutData, loadingPayout, fetchPayoutData] = usePayoutAccount();
    const [payoutModalOpen, setPayoutModalOpen] = useState(false);
    const [billPayoutModalOpen, setBillPayoutModalOpen] = useState(false);
    const [editingSection, setEditingSection] = useState<number | null>(null);

    const billingPoliciesRef = useRef<any>(null);
    const practiceProfileRef = useRef<any>(null);
    const practiceAddressRef = useRef<any>(null);
    const consentFormsRef = useRef<any>(null);

    const [billPayoutData, setBillPayoutData] = useState<ProviderBillPayoutDetailVo | undefined>(undefined);
    const [loadingBillPayout, setLoadingBillPayout] = useState(false);

    React.useEffect(() => {
        if (useStripe) return;
        let mounted = true;
        (async () => {
            try {
                setLoadingBillPayout(true);
                const res = await getProviderBillPayoutDetail();
                if (!res.error && mounted) {
                    setBillPayoutData(res?.data?.data);
                }
            } finally {
                if (mounted) setLoadingBillPayout(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [useStripe]);

    const loading = useStripe ? loadingPayout : loadingBillPayout;

    const handleCancel = () => setEditingSection(null);

    const handleSave = async (sectionId: number) => {
        try {
            switch (sectionId) {
                case 1:
                    await billingPoliciesRef.current?.onSubmit();
                    break;
                case 2:
                    await practiceProfileRef.current?.onSubmit();
                    break;
                case 3:
                    await practiceAddressRef.current?.onSubmit();
                    break;
                case 4:
                    await consentFormsRef.current?.onSubmit();
                    break;
                default:
                    break;
            }
            setEditingSection(null);
        } catch (error) {
            console.error(error);
        }
    };

    const handleSubmit = async () => {
        trackHeapEvent('WebApp - PPA Advance Setting Review - Click - Submit');
        if (!useStripe) {
            try {
                await submitProviderBillPayoutToBill();
            } catch (e) {
                console.error('submitProviderBillPayoutToBill failed', e);
            }
        }
        onSubmit();
    };

    const renderSectionExtra = (sectionId: number) => {
        if (editingSection === sectionId) {
            return (
                <Space>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button type="primary" onClick={() => handleSave(sectionId)}>
                        Save changes
                    </Button>
                </Space>
            );
        }
        return (
            <Button className={s.editBtn} onClick={() => setEditingSection(sectionId)}>
                Edit
            </Button>
        );
    };

    const renderSectionContent = (sectionId: number) => {
        const isEdit = editingSection === sectionId;
        switch (sectionId) {
            case 1:
                return <BillingPoliciesStep ref={billingPoliciesRef} isReview isEdit={isEdit} />;
            case 2:
                return (
                    <PracticeProfileStep
                        ref={practiceProfileRef}
                        isReview
                        isEdit={isEdit}
                        showPracticeAddress={false}
                    />
                );
            case 3:
                return (
                    <PracticeProfileStep ref={practiceAddressRef} isReview isEdit={isEdit} showPracticeForm={false} />
                );
            case 4:
                return <ConsentFormsStep ref={consentFormsRef} isReview isEdit={isEdit} />;
            default:
                return null;
        }
    };

    return (
        <Spin spinning={loading}>
            <div className={s.wrap}>
                <div className={s.header}>
                    <h2>Review your no-show policy settings</h2>
                    <p className={s.subtitle}>
                        Please confirm your practice details before moving forward. You&rsquo;ll be able to make changes
                        later if needed.
                    </p>
                </div>

                <div className={s.sections}>
                    {useStripe ? (
                        <PayoutAccountCard
                            data={payoutData}
                            onSetUp={onGoToPayoutSetup}
                            onSetUpIfNotReady={() => setPayoutModalOpen(true)}
                        />
                    ) : (
                        <BillPayoutAccountCard data={billPayoutData} onSetUp={() => setBillPayoutModalOpen(true)} />
                    )}
                    {SECTIONS.map((section) => (
                        <div key={section.id} className={s.card}>
                            <div className={s.cardHeader}>
                                <span className={s.cardTitle}>{section.title}</span>
                                {renderSectionExtra(section.id)}
                            </div>
                            <div className={s.cardBody}>{renderSectionContent(section.id)}</div>
                        </div>
                    ))}
                </div>

                <div className={s.actions}>
                    <Button onClick={onBack} className={s.backBtn}>
                        Back
                    </Button>
                    <Button type="primary" onClick={handleSubmit} className={s.submitBtn}>
                        Submit
                    </Button>
                </div>
            </div>
            {useStripe ? (
                <PayoutSetupModal
                    open={payoutModalOpen}
                    onClose={() => setPayoutModalOpen(false)}
                    initialValue={payoutData}
                    onSaved={() => fetchPayoutData()}
                />
            ) : (
                <BillPayoutSetupModal
                    open={billPayoutModalOpen}
                    onClose={() => setBillPayoutModalOpen(false)}
                    initialValue={billPayoutData}
                    onSaved={() => {
                        setBillPayoutModalOpen(false);
                        getProviderBillPayoutDetail().then((res) => {
                            if (!res.error) setBillPayoutData(res?.data?.data);
                        });
                    }}
                />
            )}
        </Spin>
    );
}
